import React, { useState, lazy, memo } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, EyeIcon } from 'lucide-react';
import { FadeIn } from './FadeIn';
const slides = [{
  label: 'Tomo 3 · Fracciones',
  title: 'El Método de la Pizza',
  desc: 'Cómo explicar fracciones equivalentes usando dibujos simples. Tu hijo entiende visualmente por qué 1/2 es lo mismo que 2/4, sin memorizar reglas vacías.',
  image: 'https://cdn.magicpatterns.com/uploads/duzQfcAKgajuJndCHdAVLh/pizza.webp'
}, {
  label: 'Tomo 3 · Multiplicación',
  title: 'La Caja de los Tesoros',
  desc: 'Un sistema visual para que la multiplicación deje de ser una tabla de memoria y se convierta en un juego de agrupar objetos. Ideal para niños visuales.',
  image: 'https://cdn.magicpatterns.com/uploads/jCtnDxiB8JB2K31a74etBL/caja_de_tesoros.webp'
}, {
  label: 'Tomo 3 · División',
  title: 'La Fiesta de los Caramelos',
  desc: 'Dividir es repartir en partes iguales. Con una analogía simple y cotidiana, tu hijo entiende la división sin frustrarse ni sentir que "no le entra".',
  image: 'https://cdn.magicpatterns.com/uploads/sz6D4UHMQmtuF3x3AACXym/la_fiesta_.webp'
}];
export function ContentPreview() {
  const [current, setCurrent] = useState(0);
  const total = slides.length;
  const next = () => setCurrent((current + 1) % total);
  const prev = () => setCurrent((current - 1 + total) % total);
  const slide = slides[current];
  return <section className="py-12 md:py-16 bg-white border-y border-slate-200" aria-label="Vista previa de métodos" data-id="element-44">
      <div className="container mx-auto px-4 max-w-5xl" data-id="element-45">
        <FadeIn data-id="element-46">
          <div className="text-center mb-8" data-id="element-47">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 mb-4 rounded-full border border-primary/30 bg-primary/10" data-id="element-48">
              <EyeIcon className="w-4 h-4 text-primary-dark" aria-hidden="true" data-id="element-49" />
              <span className="text-primary-dark text-[10px] md:text-xs font-bold uppercase tracking-widest" data-id="element-50">
                Mirá adentro del sistema
              </span>
            </div>
            <h2 className="font-heading text-3xl md:text-4xl font-extrabold text-slate-900 mb-4" data-id="element-51">
              Un vistazo a los Métodos Visuales
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto text-base" data-id="element-52">
              Ejemplos reales de cómo transformar conceptos abstractos en
              imágenes claras que tu hijo puede entender en minutos.
            </p>
          </div>
        </FadeIn>

        <FadeIn delay={0.1} data-id="element-53">
          <div className="relative max-w-4xl mx-auto" data-id="element-54">
            {/* Main Card */}
            <div className="bg-surfaceLight/50 rounded-3xl border border-slate-100 p-4 md:p-6 shadow-soft" data-id="element-55">
              <div className="grid md:grid-cols-2 gap-5 md:gap-8 items-center" data-id="element-56">
                {/* Method illustration */}
                <div className="relative order-1" data-id="element-57">
                  <div className="absolute inset-0 z-0 pointer-events-none" aria-hidden="true" style={{
                  background: 'radial-gradient(circle at center, rgba(45,212,191,0.18) 0%, rgba(45,212,191,0) 70%)',
                  filter: 'blur(20px)'
                }} data-id="element-58" />
                  <img key={slide.image} src={slide.image} alt={`${slide.title} — método visual del ${slide.label}`} className="relative z-10 w-full h-auto rounded-2xl shadow-soft border border-white" width={1024} height={1024} loading="lazy" decoding="async" data-id="element-59" />
                </div>

                {/* Text */}
                <div className="order-2" data-id="element-60">
                  <span className="inline-block text-xs font-bold text-primary-dark bg-white px-3 py-1.5 rounded-xl mb-4 shadow-sm border border-slate-100" data-id="element-61">
                    {slide.label}
                  </span>
                  <h3 className="font-heading text-2xl md:text-3xl font-extrabold text-slate-900 mb-3 leading-tight" data-id="element-62">
                    {slide.title}
                  </h3>
                  <p className="text-slate-700 text-base leading-snug mb-5" data-id="element-63">
                    {slide.desc}
                  </p>

                  {/* Desktop Navigation */}
                  <div className="hidden md:flex gap-2" data-id="element-64">
                    <button onClick={prev} className="w-10 h-10 rounded-full bg-white border border-slate-200 text-slate-600 flex items-center justify-center hover:border-primary hover:text-primary transition-colors shadow-sm" aria-label="Anterior" data-id="element-65">
                      <ChevronLeftIcon className="w-5 h-5" data-id="element-66" />
                    </button>
                    <button onClick={next} className="w-10 h-10 rounded-full bg-white border border-slate-200 text-slate-600 flex items-center justify-center hover:border-primary hover:text-primary transition-colors shadow-sm" aria-label="Siguiente" data-id="element-67">
                      <ChevronRightIcon className="w-5 h-5" data-id="element-68" />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Mobile Navigation */}
            <div className="flex md:hidden justify-center gap-4 mt-5" data-id="element-69">
              <button onClick={prev} className="w-10 h-10 rounded-full bg-white border border-slate-200 text-slate-600 flex items-center justify-center hover:border-primary hover:text-primary transition-colors shadow-sm" aria-label="Anterior" data-id="element-70">
                <ChevronLeftIcon className="w-5 h-5" data-id="element-71" />
              </button>
              <div className="flex items-center text-slate-500 font-bold text-sm" data-id="element-72">
                {current + 1} / {total}
              </div>
              <button onClick={next} className="w-10 h-10 rounded-full bg-white border border-slate-200 text-slate-600 flex items-center justify-center hover:border-primary hover:text-primary transition-colors shadow-sm" aria-label="Siguiente" data-id="element-73">
                <ChevronRightIcon className="w-5 h-5" data-id="element-74" />
              </button>
            </div>

            {/* Progress Dots */}
            <div className="flex justify-center gap-2 mt-6" data-id="element-75">
              {slides.map((_, i) => <button key={i} onClick={() => setCurrent(i)} className={`h-2.5 rounded-full transition-all duration-300 ${i === current ? 'w-8 bg-primary' : 'w-2.5 bg-slate-200 hover:bg-slate-300'}`} aria-label={`Ir a diapositiva ${i + 1}`} data-id="element-76" />)}
            </div>
          </div>
        </FadeIn>
      </div>
    </section>;
}